import { useHead } from '@unhead/vue'

export const SITE_URL = 'https://questions-finder.emindemiri.com'
export const SITE_NAME = 'UQM - University Questions Manager'
export const OG_IMAGE = `${SITE_URL}/og-image.png`

/**
 * True quando la pagina è renderizzata da puppeteer durante `pnpm run build`.
 * Il flag arriva da `inject` in vue.config.js; il controllo sullo user agent è
 * una rete di sicurezza nel caso l'iniezione cambi nome nelle versioni future.
 */
export function isPrerendering() {
  if (typeof window === 'undefined') return true
  if (window.__PRERENDER_INJECTED?.prerender) return true
  return /HeadlessChrome/.test(window.navigator?.userAgent ?? '')
}

/**
 * Registra title, description, canonical, Open Graph e JSON-LD per la route corrente.
 *
 * I meta specifici della pagina NON stanno più in public/index.html: li possiede
 * unhead, così ogni route prerenderizzata riceve i propri senza duplicati.
 */
export function useSeo({ title, description, path, image = OG_IMAGE, imageAlt, jsonLd = [], noindex = false }) {
  const url = `${SITE_URL}${path}`
  const alt = imageAlt ?? title

  useHead({
    title,
    link: [{ rel: 'canonical', href: url }],
    meta: [
      { name: 'description', content: description },
      ...(noindex ? [{ name: 'robots', content: 'noindex, follow' }] : []),

      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: SITE_NAME },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: url },
      { property: 'og:locale', content: 'it_IT' },
      { property: 'og:image', content: image },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { property: 'og:image:alt', content: alt },

      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: image },
      { name: 'twitter:image:alt', content: alt }
    ],
    script: jsonLd.map((node) => ({
      type: 'application/ld+json',
      innerHTML: JSON.stringify(node)
    }))
  })
}

/** Scheda prodotto dell'estensione: abilita i rich result "applicazione". */
export function softwareApplicationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'UQM - University Questions Manager',
    applicationCategory: 'BrowserApplication',
    operatingSystem: 'Chrome, Edge, Brave, Opera',
    url: SITE_URL,
    description:
      "Estensione Chrome che manda in autoplay le videolezioni delle università telematiche Pegaso, Mercatorum e San Raffaele, avanzando da sola da un capitolo al successivo.",
    inLanguage: 'it-IT',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'EUR',
      description: 'Utilizzo gratuito; contributo volontario per matricola/anno accademico.'
    }
  }
}

/** Le FAQ diventano un accordion espandibile direttamente nella SERP. */
export function faqSchema(faqs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a }
    }))
  }
}

/** Guida passo-passo: candidata ai rich result "HowTo". */
export function howToSchema({ name, description, steps }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name,
    description,
    inLanguage: 'it-IT',
    step: steps.map((step, i) => ({
      '@type': 'HowToStep',
      position: i + 1,
      name: step.name,
      text: step.text
    }))
  }
}

/** Briciole di pane: Google le mostra al posto dell'URL nuda. */
export function breadcrumbSchema(crumbs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map(({ name, path }, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name,
      item: `${SITE_URL}${path}`
    }))
  }
}
